<template>
  <div>

    <div class="box">
      <div class="flex justify-center">
        <div class="w-full md:w-1/2 text-center">
          <easy-date
            v-model="easyDateValue"
            :identifier.sync="easyIdentifier"
          ></easy-date>
          <div class="flex mt-4 items-center">
            <div class="h-10 px-4 py-2 rounded-l bg-red-500 text-white border border-r-0 border-theme-200 light:border-theme-600 focus:outline-none" @click="clear">
              <i class="fas fa-times"></i>
            </div>
            <flat-pickr
              v-model="dateRange.value"
              :config="dateRange.config.flatpickrConfig"
              class="form-control form-no-outline h-10 border-r-0 rounded-none"
              placeholder="Select date range"
              id="date-range"
              name="date_range"
            ></flat-pickr>
            <button
              type="button"
              class="h-10 px-5 flex items-center border border-purple-400 light:border-purple-600 rounded-r focus:outline-none hover:bg-purple-800 hover:border-purple-700"
              :class="dateRange.value ? 'bg-purple-500 text-white' : ''"
              @click="search"
            >
              <i class="fas fa-search"></i>
              <span class="ml-2">Search</span>
            </button>
          </div>
        </div>
      </div>

      <div class="w-full lg:w-1/2 mx-auto mt-4 flex flex-wrap justify-between text-center" v-if="payments.totals.income || payments.totals.expense">
        <div class="mt-4 leading-tight">
          <p class="font-bold">Total Income:</p>
          <p class="text-2xl font-light text-green-500">{{ $formatMoney(payments.totals.income)}}</p>
        </div>
        <div class="mt-4 leading-tight">
          <p class="font-bold">Total Expenses:</p>
          <p class="text-2xl font-light text-red-500">{{ $formatMoney(payments.totals.expense)}}</p>
        </div>
        <div class="mt-4 leading-tight">
          <p class="font-bold">Total Revenue:</p>
          <p class="text-2xl font-light text-purple-200 light:text-purple-700">{{ $formatMoney(payments.totals.income - payments.totals.expense)}}</p>
        </div>
      </div>

      <div class="w-full lg:w-10/12 mx-auto mt-4" v-if="payments.income.length || payments.expense.length">
        <highcharts :options="highChartOptions"></highcharts>
      </div>
      <p class="text-lg text-center my-12" v-else-if="!payments.income.length && !payments.expense.length">No data to display</p>

    </div>

  </div>
</template>

<script>
import axios from 'axios'
const EasyDate = resolve => require(['@/components/EasyDate'], resolve)
import { mapGetters } from 'vuex'

export default {
  components: {
    EasyDate,
  },
  data() {
    return {
      easyDateValue: '',
      easyIdentifier: '',
      dateRange: {
        value: '',
        config: {
          flatpickrConfig: {}
        }
      },
      payments: {
        totals: [],
        income: [],
        expense: [],
      },
      highChartOptions: {
        credits: {
          enabled: false
        },
        chart: {
          type: 'line'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
          type: 'day',
          dateTimeLabelFormats: {
            day: '%d/%m/%y'
          },
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        series: [],
        tooltip: {
          formatter: function() {
            return this.x + '<br>' + this.point.series.name + ' <b>' + window.accounting.formatMoney(this.y) + '</b>'
          }
        }
      },
    }
  },
  watch: {
    easyDateValue(dates) {
      if (!dates.from || !dates.to) {
        return
      }
      this.dateRange.value = dates.from.format(this.config.dateFormats.display) + ' to ' + dates.to.format(this.config.dateFormats.display)
      this.get()
    }
  },
  computed: {
    ...mapGetters({
      config: 'config/config',
    })
  },
  created() {
    this.dateRange.config.flatpickrConfig = Object.assign({}, this.config.flatpickrConfig)
    this.dateRange.config.flatpickrConfig.mode = 'range'
    this.dateRange.config.flatpickrConfig.static = false
  },
  mounted() {
    this.easyIdentifier = 'current-month'
  },
  methods: {
    clear() {
      this.dateRange.value = ''
      this.easyIdentifier = ''
      this.payments.totals = []
      this.payments.income = []
      this.payments.expense = []
    },
    search() {
      this.easyIdentifier = ''
      this.get()
    },
    async get() {
      let dateRange = this.$getRangeDates(this.dateRange.value, this.config)

      const total = await axios.post('reports/total/date', {
        from: dateRange.from,
        to: dateRange.to,
      })
      const income = await axios.post('reports/payments/grouped', {
        from: dateRange.from,
        to: dateRange.to,
        type: 'income',
        group: 'day',
      })
      const expense = await axios.post('reports/payments/grouped', {
        from: dateRange.from,
        to: dateRange.to,
        type: 'expense',
        group: 'day',
      })

      Promise.all([total, income, expense])
        .then(values => {
          this.payments.totals = this.reorder(values[0].data)
          this.payments.income = values[1].data
          this.payments.expense = values[2].data


          let labels = []
          this.payments.income.map(e => {
            labels.push(e.date_group)
          })
          this.payments.expense.map(e => {
            for (let i in labels) {
              if (labels[i] === e.date_group) {
                return
              }
            }
            labels.push(e.date_group)
          })
          labels.sort((a, b) => this.$moment(a, this.config.dateFormats.display) - this.$moment(b, this.config.dateFormats.display))


          let incomeData = []
          let expenseData = []
          for (let i in labels) {
            labels[i]
            let inc = this.payments.income.filter(e => e.date_group === labels[i])[0]
            let exp = this.payments.expense.filter(e => e.date_group === labels[i])[0]
            incomeData.push(inc ? parseFloat(inc.total) : null)
            expenseData.push(exp ? parseFloat(exp.total) : null)
          }

          this.highChartOptions.xAxis.categories = labels
          this.highChartOptions.series = [
            {
              name: 'Income',
              data: incomeData,
              connectNulls: true,
            },
            {
              name: 'Expense',
              data: expenseData,
              connectNulls: true,
            }
          ]

        })
    },
    reorder(totals) {
      let income = totals.filter(t => t.type === 'income')
      let expense = totals.filter(t => t.type === 'expense')
      return {
        income: income && income.length ? income[0].total : 0,
        expense: expense && expense.length ? expense[0].total : 0,
      }
    },
  }
}
</script>
